import React, { useEffect, useState, useRef, useCallback } from 'react'
import CommonApi from '@/pages/api'
import Header from '@/src/components/announcement-header'
import { Footer } from '@/src/components/footer'
import ListContainer from './components/ListContainer'
import { AnnouncementEndpoint } from '@/pages/api/endpoints/Announcement'
import { useRouter } from 'next/router'
import Dashboard from './components/dashboard'
import { useStore } from '@/src/types/store'
import { useDebounceCallback } from '@/src/types/UseDebounceHook'
import { ManageContents } from '@/pages/api/endpoints'
import { staticIressCategory } from './components/dashboard/utils'
import { CircularProgress } from '@mui/material'
import NewFilterContainer from './components/NewFilterContainer'
import NewsInsights from './components/news-insights'
import {
  useAnnouncementFiltersStore,
  defaultAnnouncementFilters
} from '@/src/store/announcementFiltersStore'
interface AnnouncementsProps {
  category: string
}

export interface getAnnouncementProps {
  labels?: [] | string | string[] | any
  categories?: [] | string | string[] | any
  years?: string | string[] | []
  page?: number
  searchKey?: string
  wantLoading?: boolean
  iress_category?: number[] | null
}
const Announcements = ({ category }: AnnouncementsProps) => {
  const { setAnnYearsList, setAnnoucementPage } = useStore()
  const router = useRouter()

  // Persisted filter state — survives detail-page navigation and refresh.
  const searchKey = useAnnouncementFiltersStore(s => s.searchKey)
  const selectedTabs = useAnnouncementFiltersStore(s => s.selectedTabs)
  const years = useAnnouncementFiltersStore(s => s.years)
  const currPage = useAnnouncementFiltersStore(s => s.currPage)
  const setFilters = useAnnouncementFiltersStore(s => s.setFilters)
  const resetFilters = useAnnouncementFiltersStore(s => s.resetFilters)

  const setSearchKey = useCallback((v: string) => setFilters({ searchKey: v }), [setFilters])
  const setSelectedTabs = useCallback(
    (v: string[]) => setFilters({ selectedTabs: v }),
    [setFilters]
  )
  const setYears = useCallback((v: string[]) => setFilters({ years: v }), [setFilters])
  const setCurrPage = useCallback((v: number) => setFilters({ currPage: v }), [setFilters])

  const [defaultFilter, setDefaultFilter] = useState('All')
  const [shouldClearFilters, setShouldClearFilters] = useState(false)
  const initialFetchDone = useRef(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [fromValue, setFromValue] = useState('')
  const [toValue, setToValue] = useState('')
  const [iressCategory, setIressCategory] = useState<number[] | null>([])
  const [defaultCategory, setDefaultCategory] = useState<
    { label: string; value: number }[] | null
  >([])
  const [announcementData, setAnnouncementData] = useState<unknown>({})
  const [selectedCategory, setSelectedCategory] = useState<string[]>([category])
  const [loading, setLoading] = useState(true)
  const [fetchLoading, setFetchLoading] = useState(false)

  const handleAnnouncement = useCallback(
    async ({
      labels = '',
      years = [],
      page = 1,
      searchKey = '',
      wantLoading = true,
      iress_category = []
    }: getAnnouncementProps) => {
      if (category === 'update') return
      if (wantLoading) setLoading(true)

      try {
        const response = await CommonApi({
          path: AnnouncementEndpoint?.get({
            searchValue: searchKey,
            fromDate: fromValue,
            toDate: toValue,
            category: category,
            iress_category: iress_category,
            filter: labels,
            years: years[0] === 'All Years' ? [] : years,
            page: page
          }),
          method: 'get'
        })
        const yearsData = [
          'All Years',
          ...response.data.years_list.sort((a: number, b: number) => b - a)
        ]
        setAnnYearsList(yearsData)
        setAnnouncementData(response.data)
      } catch (error) {
        console.error('err', error)
      }
      setLoading(false)
    },
    [category, fromValue, toValue, setAnnYearsList]
  )

  const handleSearch = useDebounceCallback((value: any) => {
    setCurrPage(1)
    setAnnoucementPage(1)
    setSearchKey(value ?? '')
    const labels = selectedTabs?.includes('All')
      ? []
      : selectedTabs?.map(item => `"${item}"`)
    const categories = selectedCategory?.map(item => `"${item}"`)
    handleAnnouncement({
      labels,
      categories,
      years: years[0] === 'All Years' ? [] : years,
      page: 1,
      searchKey: value,
      iress_category: iressCategory
    })
  }, 500)

  useEffect(() => {
    if (!router.isReady || isInitialLoad) return
    if (category === 'dashboard') return
    // Initial resolution only. Every later fetch is driven by the filter handlers,
    // so this must not re-run when the URL or a callback identity changes —
    // otherwise a tab click on ?filter=presentations gets snapped back.
    if (initialFetchDone.current) return
    initialFetchDone.current = true

    // URL deep-link (?filter=…) overrides persisted filters.
    // Without a deep-link, rehydrate from the Zustand store and fetch with those values.
    const isUrlDeepLink = defaultFilter !== 'All'

    if (isUrlDeepLink && defaultFilter !== 'reports') {
      const tab = defaultFilter === 'presentations' ? 'Presentation' : defaultFilter
      // Deep-link decides the whole filter state: drop any persisted year/search/page
      // so the UI matches what is actually being fetched.
      setFilters({
        selectedTabs: [tab],
        years: defaultAnnouncementFilters.years,
        searchKey: defaultAnnouncementFilters.searchKey,
        currPage: 1
      })
      setAnnoucementPage(1)
      handleAnnouncement({
        labels:
          defaultFilter === 'presentations'
            ? `"Presentation"`
            : [defaultFilter].map(item => `"${item}"`),
        categories: `"${category}"`,
        years: []
      })
    } else if (defaultFilter === 'reports') {
      // Fetch itself is handled by fetchIressCategories → filterIressCategory,
      // which reads year/search from the store — so clear them here too.
      setFilters({
        selectedTabs: ['Company Reports'],
        years: defaultAnnouncementFilters.years,
        searchKey: defaultAnnouncementFilters.searchKey,
        currPage: 1
      })
      setAnnoucementPage(1)
    } else if (shouldClearFilters) {
      // Explicit ?filter=All (e.g. "More announcements") starts clean —
      // ignore whatever was persisted from the previous visit.
      resetFilters()
      setAnnoucementPage(1)
      setSelectedCategory([category])
      handleAnnouncement({
        labels: '',
        categories: `"${category}"`,
        years: [],
        page: 1,
        searchKey: ''
      })
    } else {
      const persisted = useAnnouncementFiltersStore.getState()
      const labels = persisted.selectedTabs.includes('All')
        ? ''
        : persisted.selectedTabs.map(item => `"${item}"`)
      handleAnnouncement({
        labels,
        categories: `"${category}"`,
        years: persisted.years[0] === 'All Years' ? [] : persisted.years,
        page: persisted.currPage,
        searchKey: persisted.searchKey
      })
    }
  }, [
    router.isReady,
    isInitialLoad,
    defaultFilter,
    shouldClearFilters,
    category,
    handleAnnouncement,
    setFilters,
    setAnnoucementPage,
    resetFilters
  ])

  const handleDate = (value: any) => {
    const dateFrom = new Date(value[0])
    const dateTwo = new Date(value[1])
    const year1 = dateFrom.getUTCFullYear()
    const month1 = ('0' + (dateFrom.getUTCMonth() + 1)).slice(-2)
    const day1 = ('0' + dateFrom.getUTCDate()).slice(-2)
    const year2 = dateTwo.getUTCFullYear()
    const month2 = ('0' + (dateTwo.getUTCMonth() + 1)).slice(-2)
    const day2 = ('0' + dateTwo.getUTCDate()).slice(-2)
    const formattedDateFrom = `${year1}-${month1}-${day1}`
    const formattedDateTo = `${year2}-${month2}-${day2}`

    setFromValue(formattedDateFrom)
    setToValue(formattedDateTo)

    setCurrPage(1)
    setAnnoucementPage(1)
    const labels = selectedTabs?.includes('All') ? [] : selectedTabs?.map(item => `"${item}"`)
    const categories = selectedCategory?.map(item => `"${item}"`)
    handleAnnouncement({
      labels,
      categories,
      years: years[0] === 'All Years' ? [] : years,
      searchKey,
      iress_category: iressCategory
    })
  }

  const handleClick = (value: string, paramsCatgory?: boolean) => {
    setCurrPage(1)
    setAnnoucementPage(1)
    const iress_category = paramsCatgory === false ? [] : iressCategory
    const categories = selectedCategory?.map(item => `"${item}"`)
    if (selectedTabs.includes(value)) {
      const filteredData = selectedTabs.filter(tab => tab !== value)
      setSelectedTabs(filteredData)
      const finalArray = filteredData?.map(item => `"${item}"`)
      handleAnnouncement({
        labels: finalArray,
        categories,
        years: years[0] === 'All Years' ? [] : years,
        searchKey,
        iress_category: iress_category
      })
    } else {
      if (value === 'All') {
        setSelectedTabs(['All'])
        if (paramsCatgory === false) {
          setSelectedCategory([category])
        }
        handleAnnouncement({
          labels: [],
          categories: paramsCatgory === false ? '' : categories,
          years: years[0] === 'All Years' ? [] : years,
          searchKey,
          iress_category: iress_category
        })
      } else {
        const data = [...selectedTabs, value].filter(d => d !== 'All')
        setSelectedTabs(data)
        const finalArray = data?.map(item => `"${item}"`)
        handleAnnouncement({
          labels: finalArray,
          categories,
          years: years[0] === 'All Years' ? [] : years,
          searchKey,
          iress_category: iress_category
        })
      }
    }
    if (defaultFilter === 'reports' || defaultFilter === 'presentations') {
      router.replace('announcements')
    }
  }

  const handleCategoryClick = (value: string, paramsCatgory?: boolean) => {
    setCurrPage(1)
    setAnnoucementPage(1)
    const labels = selectedTabs?.map(item => `"${item}"`)
    const category = paramsCatgory === false ? [] : iressCategory
    if (selectedCategory.includes(value)) {
      const filteredData = selectedCategory.filter(tab => tab !== value)
      setSelectedCategory(filteredData)
      const finalArray = filteredData?.map(item => `"${item}"`)
      handleAnnouncement({
        labels,
        categories: finalArray,
        years: years[0] === 'All Years' ? [] : years,
        searchKey,
        iress_category: category
      })
    } else {
      if (value === 'All') {
        setSelectedCategory(['All'])
        handleAnnouncement({
          labels,
          categories: '',
          years: years[0] === 'All Years' ? [] : years,
          searchKey,
          iress_category: category
        })
      } else {
        const data = [...selectedCategory, value].filter(d => d !== 'All')
        setSelectedCategory(data)
        const finalArray = data?.map(item => `"${item}"`)
        handleAnnouncement({
          labels,
          categories: finalArray,
          years: years[0] === 'All Years' ? [] : years,
          searchKey,
          iress_category: category
        })
      }
    }
  }

  const handleYearChange = (val: string[]) => {
    setCurrPage(1)
    setAnnoucementPage(1)
    const labels = selectedTabs?.map(item => `"${item}"`)
    const categories = selectedCategory?.map(item => `"${item}"`)
    if ((val[0] !== 'All Years' && val.includes('All Years')) || val.length === 0) {
      handleAnnouncement({
        labels,
        categories,
        years: [],
        searchKey,
        iress_category: iressCategory
      })
      setYears(['All Years'])
    } else {
      const filteredVal = val.filter(d => d !== 'All Years')
      handleAnnouncement({
        labels,
        categories,
        years: filteredVal,
        searchKey,
        iress_category: iressCategory
      })
      setYears(filteredVal)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrPage(page)
    setAnnoucementPage(page)

    const labels = selectedTabs?.includes('All') ? [] : selectedTabs?.map(item => `"${item}"`)
    const categories = selectedCategory?.map(item => `"${item}"`)
    const isEmptyOrPresentationOnly =
      Array.isArray(labels) &&
      (labels.length === 0 || (labels.length === 1 && labels[0] === `"Presentation"`))

    handleAnnouncement({
      labels,
      categories,
      years: years[0] === 'All Years' ? [] : years,
      page,
      searchKey,
      iress_category: isEmptyOrPresentationOnly ? [] : iressCategory
    })
  }

  const filterIressCategory = useCallback(
    (category: number[], isReport?: boolean) => {
      setCurrPage(1)
      setAnnoucementPage(1)
      let labels
      if (isReport) {
        labels = [`"Company Reports"`]
      } else {
        labels = selectedTabs?.map(item => `"${item}"`)
      }
      const categories = selectedCategory?.map(item => `"${item}"`)
      const ireesCategory = category?.length ? category : null
      setIressCategory(ireesCategory)
      handleAnnouncement({
        labels,
        categories,
        years,
        searchKey,
        iress_category: ireesCategory
      })
    },
    [selectedTabs, selectedCategory, years, searchKey, handleAnnouncement, setAnnoucementPage]
  )

  const handleClearAll = useCallback(() => {
    setAnnoucementPage(1)
    // Reset filter tabs / years / page but preserve the search input — matches
    // pre-existing behavior where Clear All never wiped the search box.
    setFilters({
      selectedTabs: defaultAnnouncementFilters.selectedTabs,
      years: defaultAnnouncementFilters.years,
      currPage: defaultAnnouncementFilters.currPage
    })
    setSelectedCategory([category])
    // Intentionally NOT resetting iressCategory: it's only set programmatically
    // by fetchIressCategories for the ?filter=reports deep-link, and clearing it
    // here would break that view with no UI to restore it.
    handleAnnouncement({
      labels: [],
      categories: '',
      years: [],
      searchKey,
      iress_category: iressCategory
    })
  }, [category, searchKey, iressCategory, handleAnnouncement, setAnnoucementPage, setFilters])

  const fetchIressCategories = useCallback(async () => {
    setFetchLoading(true)
    try {
      const resp = await CommonApi({
        path: ManageContents.fetchIresscategory('', false),
        method: 'get'
      })
      if (resp?.status) {
        const formatResponse = resp?.data?.map((item: any) => ({
          label: item?.name,
          value: item?.category_id
        }))
        const matchedItems = (formatResponse ?? []).filter((item: any) =>
          (staticIressCategory ?? []).includes(item.label)
        )
        if (defaultFilter === 'reports') {
          setDefaultCategory(matchedItems)
          filterIressCategory(
            matchedItems.map((item: any) => item.value),
            true
          )
        } else {
          setDefaultCategory([])
        }
      }
    } catch (error) {
      // Don't rethrow — an uncaught rejection here crashes the whole screen
      // (e.g. when every call 500/503s on a deactivated domain). Log and move on.
      console.error('Failed to fetch IRSS Category in Announcement', error)
    } finally {
      setFetchLoading(false)
    }
  }, [defaultFilter, filterIressCategory])

  useEffect(() => {
    if (category !== 'update') {
      fetchIressCategories()
    }
  }, [defaultFilter, router.query, fetchIressCategories, category])

  useEffect(() => {
    if (!router.isReady || !isInitialLoad) return

    const filterParam = router.query.filter
    const rawFilter = Array.isArray(filterParam) ? filterParam[0] : filterParam

    setDefaultFilter(rawFilter || 'All')
    // Explicit ?filter=All (e.g. "More announcements") means "start clean",
    // as opposed to no param at all, which rehydrates the persisted filters.
    setShouldClearFilters(rawFilter === 'All')
    setIsInitialLoad(false)
  }, [router.isReady])

  return (
    <>
      <Header />
      <div
        className={`lg:max-w-screen overflow-y-auto bg-[#f5f5f8]`}
        style={{
          height: 'calc(100vh - 100px)',
          marginTop: '6rem',
          transition: 'margin-top 1s ease-in-out'
        }}
      >
        <div>
          {category === 'dashboard' ? (
            <Dashboard />
          ) : category === 'update' ? (
            <NewsInsights />
          ) : (
            <div className="flex flex-col min-h-[calc(100vh-164px)] py-8 px-6 lg:px-[104px] xl:px-[124px] 2xl:px-[164px]">
              <NewFilterContainer
                handleSearch={handleSearch}
                selectedTabs={selectedTabs}
                handleClick={handleClick}
                handleYearChange={handleYearChange}
                years={years}
                handleClearAll={handleClearAll}
                searchKey={searchKey}
              />
              {/* list container */}
              {fetchLoading ? (
                <div className="w-full pb-[2.125rem] flex justify-center items-center">
                  <CircularProgress size={30} />
                </div>
              ) : (
                <ListContainer
                  showDatePicker={false}
                  handleDate={handleDate}
                  fromValue={fromValue}
                  toValue={toValue}
                  loading={loading}
                  announcementData={announcementData}
                  handlePageChange={handlePageChange}
                  currPage={currPage}
                  handleAnnouncement={handleAnnouncement}
                />
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  )
}
export default Announcements
